import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useListenerRecording } from "@/hooks/recording/useListenerRecording";
import { Agent } from "@/types/agent/agent";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { BRAND_MASCOT } from "@/data/brandMascots";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const MAX_SPEAKERS = 32;

interface ListenerExperienceProps {
  agent: Agent;
}

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export const ListenerExperience = ({ agent }: ListenerExperienceProps) => {
  const navigate = useNavigate();
  const language = agent.settings?.conversation_config?.agent?.language ?? "en";
  const [numSpeakers, setNumSpeakers] = useState<number | undefined>(undefined);

  const { status, elapsedSeconds, conversationId, errorMessage, startRecording, stopRecording, reset } =
    useListenerRecording({ agent, language, numSpeakers });

  const logoUrl = resolveGenieAvatarUrl(agent) ?? BRAND_MASCOT;
  const primary = agent.branding?.primaryColor ?? "#4E9CFF";
  const secondary = agent.branding?.secondaryColor ?? "#FF4DB8";
  const gradientStyle = { background: `linear-gradient(135deg, ${primary}, ${secondary})` };
  const tintBg = `${primary}1a`;

  const MascotDisc = ({ size = 112 }: { size?: number }) => (
    <div
      className="rounded-full flex items-center justify-center overflow-hidden shadow-xl"
      style={{ width: size, height: size, backgroundColor: tintBg }}
    >
      <img src={logoUrl} alt={agent.name} className="w-4/5 h-4/5 object-contain" />
    </div>
  );

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#F9FAFC] px-4">
      <AnimatePresence mode="wait">
        {/* IDLE */}
        {status === "idle" && (
          <motion.div
            key="idle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="flex flex-col items-center text-center gap-6 max-w-sm"
          >
            <button
              onClick={startRecording}
              className="hover:scale-105 transition-transform duration-200"
            >
              <MascotDisc size={112} />
            </button>

            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-1">{agent.name}</h2>
              <p className="text-sm text-gray-500">Tap to start recording your meeting</p>
            </div>

            <p className="text-xs text-gray-400 max-w-xs">
              Your Genie will transcribe the conversation with speaker labels and deliver a full recap to your Inbox.
            </p>

            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">Number of speakers (optional)</span>
              <div className="flex items-center gap-2 rounded-full border border-gray-200 px-1 py-1">
                <button
                  type="button"
                  onClick={() => setNumSpeakers((prev) => (prev === undefined ? undefined : prev <= 2 ? undefined : prev - 1))}
                  disabled={numSpeakers === undefined}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  aria-label="Decrease speaker count"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="w-5 text-center text-sm font-medium text-gray-700">{numSpeakers ?? "Auto"}</span>
                <button
                  type="button"
                  onClick={() => setNumSpeakers((prev) => Math.min((prev ?? 1) + 1, MAX_SPEAKERS))}
                  disabled={numSpeakers === MAX_SPEAKERS}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  aria-label="Increase speaker count"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* RECORDING */}
        {status === "recording" && (
          <motion.div
            key="recording"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="flex flex-col items-center text-center gap-6 max-w-sm"
          >
            <div className="relative w-28 h-28 flex items-center justify-center">
              <span className="absolute inset-0 rounded-full opacity-20 animate-ping" style={gradientStyle} />
              <span className="absolute inset-2 rounded-full opacity-20 animate-ping [animation-delay:0.3s]" style={gradientStyle} />
              <MascotDisc size={112} />
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: primary }}>Recording</p>
              <p className="text-3xl font-mono font-semibold text-gray-800">{formatTime(elapsedSeconds)}</p>
            </div>

            <Button
              onClick={stopRecording}
              variant="outline"
              className="rounded-full px-8 border-gray-300 text-gray-700 hover:border-red-400 hover:text-red-500 transition-colors"
            >
              Stop Recording
            </Button>

            <p className="text-xs text-gray-400">Keep this tab open while recording</p>
          </motion.div>
        )}

        {/* UPLOADING / PROCESSING */}
        {(status === "uploading" || status === "processing") && (
          <motion.div
            key="processing"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="flex flex-col items-center text-center gap-5 max-w-sm"
          >
            <MascotDisc size={80} />

            <div>
              <p className="text-lg font-semibold text-gray-800 mb-1">
                {status === "uploading" ? "Uploading…" : "Transcribing your meeting…"}
              </p>
              <p className="text-sm text-gray-500">
                {status === "uploading"
                  ? "Sending your recording securely"
                  : "This usually takes a minute or two. You can close this tab — your recap will appear in the Inbox when ready."}
              </p>
            </div>

            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-2 h-2 rounded-full animate-bounce"
                  style={{ ...gradientStyle, animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>

            {status === "processing" && (
              <Button
                onClick={() => navigate("/genie-hub/conversations")}
                variant="outline"
                className="rounded-full px-8 border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 transition-colors"
              >
                Open Inbox
              </Button>
            )}
          </motion.div>
        )}

        {/* DONE */}
        {status === "done" && (
          <motion.div
            key="done"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center text-center gap-6 max-w-sm"
          >
            <div className="relative">
              <MascotDisc size={80} />
              <div
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center shadow-md"
                style={gradientStyle}
              >
                <GenieIcon name="check-circle" size={16} className="w-4 h-4" />
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-1">Your recap is ready</h2>
              <p className="text-sm text-gray-500">
                Transcript, decisions, action items, and key takeaways are waiting in your Inbox.
              </p>
            </div>

            <Button
              onClick={() => navigate("/genie-hub/conversations")}
              className="text-white border-0 hover:opacity-90 transition-opacity px-8"
              style={gradientStyle}
            >
              Open Inbox
            </Button>

            <button
              onClick={reset}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Record another meeting
            </button>
          </motion.div>
        )}

        {/* ERROR */}
        {status === "error" && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center text-center gap-5 max-w-sm"
          >
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center border border-red-100 overflow-hidden">
              <img src={logoUrl} alt="" className="w-4/5 h-4/5 object-contain opacity-50" />
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Something went wrong</h2>
              <p className="text-sm text-gray-500">{errorMessage ?? "An unexpected error occurred."}</p>
            </div>

            <Button
              onClick={reset}
              variant="outline"
              className="rounded-full px-8"
            >
              Try again
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
